import { useCallback, useEffect, useMemo, useState } from 'react';

// OVE-84 — Odoo product proposal queue.
//
// Vendor/product changes arrive as PROPOSALS from on-ramps (paste-grid, texted
// URL) and land here for a human to approve/reject. Approve → the server writes
// a product.template to Odoo. Package readiness and BOM population live in Build
// Studio, not Sales-QA. Margin-first: every row leads with retail → cost = margin
// so a stale/thin margin is obvious. Provenance dots show, per field, whether a
// value is vendor data, AI-inferred, or human-edited.
//
// IA mirrors infra/docs/catalog-integrity/mockup-v3-review-queue.html, re-skinned
// to the sales-qa light theme for consistency with the rest of the app.

type FieldSource = 'vendor' | 'ai' | 'human';

interface Proposal {
  id: string;
  source_onramp: string;
  created_by: string;
  created_at: string;
  vendor: string | null;
  source_url: string | null;
  name: string;
  sku: string | null;
  barcode: string | null;
  image_url: string | null;
  description_sale: string | null;
  retail_price: string | null;
  dealer_cost: string | null;
  margin_abs: number | null;
  margin_pct: number | null;
  thin_margin: boolean;
  change_type: 'new_product' | 'cost_update';
  target_template_id: number | null;
  current_cost: string | null;
  cost_delta_abs: number | null;
  cost_delta_pct: number | null;
  current_margin_pct: number | null;
  dedup_template_id: number | null;
  field_provenance: Record<string, FieldSource>;
  scrape_warnings: string[];
  status: 'pending' | 'applying' | 'reconciliation_required' | 'approved' | 'rejected';
  review_note: string | null;
}

interface Health {
  pending: number;
  thin_margin: number;
  approved_today: number;
  catalog_health_pct: number | null;
}

type Banner = { kind: 'ok' | 'err'; text: string } | null;

const ONRAMP_LABELS: Record<string, string> = {
  paste_grid: 'Paste grid',
  text_url: 'Texted URL',
  price_sheet: 'Price sheet',
  quote_part: 'Quote part',
  watchdog: 'Watchdog',
};

const TABS: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'paste_grid', label: 'Paste grid' },
  { key: 'text_url', label: 'Texted URL' },
  { key: 'price_sheet', label: 'Price sheet' },
];

function money(v: string | null): string {
  if (v == null) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
}

// ── Provenance dot ──────────────────────────────────────────────────────────
function ProvenanceDot({ field, source }: { field: string; source: FieldSource | undefined }): JSX.Element {
  const cfg: Record<FieldSource, { cls: string; label: string }> = {
    vendor: { cls: 'bg-slate-400', label: 'vendor data' },
    ai: { cls: 'bg-amber-400 ring-1 ring-amber-500', label: 'AI-inferred' },
    human: { cls: 'bg-emerald-500 ring-2 ring-emerald-200', label: 'human-edited' },
  };
  const c = source ? cfg[source] : { cls: 'bg-slate-200', label: 'no value' };
  return (
    <span className="inline-flex items-center gap-1" title={`${field}: ${c.label}`}>
      <span className={`inline-block w-2 h-2 rounded-full ${c.cls}`} />
      <span className="text-[11px] text-slate-400">{field}</span>
    </span>
  );
}

export function ProductProposalQueue(): JSX.Element {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [health, setHealth] = useState<Health | null>(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<Banner>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<string>('all');
  const [pasteText, setPasteText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, applyingRes, reconciliationRes, hRes] = await Promise.all([
        fetch('/api/catalog/proposals?status=pending&limit=300', { credentials: 'include' }),
        fetch('/api/catalog/proposals?status=applying&limit=100', { credentials: 'include' }),
        fetch('/api/catalog/proposals?status=reconciliation_required&limit=100', { credentials: 'include' }),
        fetch('/api/catalog/health', { credentials: 'include' }),
      ]);
      if (!pRes.ok || !applyingRes.ok || !reconciliationRes.ok) {
        throw new Error(`proposals: status ${pRes.status}/${applyingRes.status}/${reconciliationRes.status}`);
      }
      const pData = (await pRes.json()) as { proposals: Proposal[] };
      const applyingData = (await applyingRes.json()) as { proposals: Proposal[] };
      const reconciliationData = (await reconciliationRes.json()) as { proposals: Proposal[] };
      setProposals([...reconciliationData.proposals, ...applyingData.proposals, ...pData.proposals]);
      if (hRes.ok) setHealth((await hRes.json()) as Health);
    } catch (err) {
      setBanner({ kind: 'err', text: `Failed to load: ${(err as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => (tab === 'all' ? proposals : proposals.filter((p) => p.source_onramp === tab)),
    [proposals, tab],
  );

  const dropRow = useCallback((id: string) => {
    setProposals((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // ── Paste-grid submit ───────────────────────────────────────────────────────
  const submitPaste = useCallback(async () => {
    const urls = pasteText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (urls.length === 0 || busy) return;
    setBusy(true);
    try {
      const res = await fetch('/api/catalog/proposals', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: urls.map((url) => ({ url })) }),
      });
      const payload = (await res.json().catch(() => ({}))) as {
        created_count?: number;
        requested_count?: number;
        results?: Array<{ ok: boolean; error?: string }>;
        error?: string;
      };
      if (!res.ok && res.status !== 201 && res.status !== 422) {
        throw new Error(String(payload.error ?? `status ${res.status}`));
      }
      const failed = (payload.results ?? []).filter((r) => !r.ok).length;
      setBanner({
        kind: failed > 0 ? 'err' : 'ok',
        text: `Added ${payload.created_count ?? 0} proposal(s)${failed > 0 ? `, ${failed} failed` : ''}.`,
      });
      setPasteText('');
      await load();
    } catch (err) {
      setBanner({ kind: 'err', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }, [pasteText, busy, load]);

  // ── Row actions ───────────────────────────────────────────────────────────────
  const approve = useCallback(
    async (p: Proposal, force = false) => {
      if (busy) return;
      setBusy(true);
      try {
        const res = await fetch(`/api/catalog/proposals/${p.id}/approve`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ force }),
        });
        const payload = (await res.json().catch(() => ({}))) as {
          odoo_template_id?: number;
          odoo_default_code?: string | null;
          error?: string;
          existing_template_id?: number;
        };
        if (res.status === 409 && payload.existing_template_id != null && !force && p.change_type !== 'cost_update') {
          const ok = window.confirm(
            `This matches existing Odoo product #${payload.existing_template_id}. Create a separate Odoo product anyway?`,
          );
          if (ok) {
            // Recurse with force WITHOUT toggling busy off first — keeps the row
            // disabled across the confirm→retry so no double-click slips through.
            return approve(p, true);
          }
          setBanner({ kind: 'err', text: `Skipped — duplicate of #${payload.existing_template_id}.` });
          return;
        }
        if (res.status === 409 && p.change_type === 'cost_update') await load();
        if (!res.ok) throw new Error(String(payload.error ?? `status ${res.status}`));
        // OVE-111: Odoo assigns its own house SKU on create (auto-SKU module) —
        // show it, since it is NOT the vendor SKU the proposal carried.
        const skuNote = payload.odoo_default_code ? ` Odoo SKU: ${payload.odoo_default_code}.` : '';
        setBanner({
          kind: 'ok',
          text: p.change_type === 'cost_update'
            ? `Approved → Odoo product #${payload.odoo_template_id} cost updated.`
            : `Approved → Odoo product #${payload.odoo_template_id} created.${skuNote}`,
        });
        dropRow(p.id);
      } catch (err) {
        setBanner({ kind: 'err', text: (err as Error).message });
      } finally {
        setBusy(false);
      }
    },
    [busy, dropRow, load],
  );

  const reject = useCallback(
    async (p: Proposal) => {
      if (busy) return;
      const note = window.prompt('Reason for rejecting? (optional)') ?? undefined;
      setBusy(true);
      try {
        const res = await fetch(`/api/catalog/proposals/${p.id}/reject`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        });
        const payload = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) throw new Error(String(payload.error ?? `status ${res.status}`));
        setBanner({ kind: 'ok', text: 'Rejected.' });
        dropRow(p.id);
      } catch (err) {
        setBanner({ kind: 'err', text: (err as Error).message });
      } finally {
        setBusy(false);
      }
    },
    [busy, dropRow],
  );

  const saveEdit = useCallback(
    async (id: string, fields: Record<string, unknown>) => {
      setBusy(true);
      try {
        const res = await fetch(`/api/catalog/proposals/${id}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fields),
        });
        const payload = (await res.json().catch(() => ({}))) as Proposal & { error?: string };
        if (!res.ok) throw new Error(String(payload.error ?? `status ${res.status}`));
        setProposals((prev) => prev.map((p) => (p.id === id ? payload : p)));
        setEditingId(null);
        setBanner({ kind: 'ok', text: 'Saved.' });
      } catch (err) {
        setBanner({ kind: 'err', text: (err as Error).message });
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const pct = (n: number | null): string => (n == null ? '—' : `${Math.round(n * 100)}%`);

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900">Product proposal queue</h2>
            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-rose-200">
              Odoo product write
            </span>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Vendor URLs and price sheets become Odoo proposals here. Review cost, margin,
            duplicate risk, SKU/barcode, and provenance before creating a product or updating its cost in Odoo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="shrink-0 rounded-md px-3 py-1.5 text-sm ring-1 ring-slate-200 hover:bg-slate-100"
        >
          Refresh
        </button>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        <BoundaryCard
          label="Intake"
          title="Product candidates only"
          body="Pasted links, texted URLs, and vendor sheets queue proposals. Intake does not create or update Odoo records."
        />
        <BoundaryCard
          label="Review"
          title="Odoo-product fit check"
          body="Thin margin, missing cost, provenance, SKU, barcode, source URL, and duplicate risk are the decision inputs."
        />
        <BoundaryCard
          label="Write"
          title="Approval writes product data"
          body="The green action creates a product or updates one approved cost. Package readiness and BOM population live in Build Studio."
          tone="write"
        />
      </div>

      {/* Health strip */}
      <div className="mt-4 mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <HealthCard label="Awaiting decision" value={health ? String(health.pending) : '—'} />
        <HealthCard
          label="Thin margin"
          value={health ? String(health.thin_margin) : '—'}
          tone={health && health.thin_margin > 0 ? 'warn' : 'default'}
        />
        <HealthCard label="Approved today" value={health ? String(health.approved_today) : '—'} tone="good" />
        <HealthCard
          label="Proposal health"
          value={health && health.catalog_health_pct != null ? pct(health.catalog_health_pct) : '—'}
          tone="good"
        />
      </div>

      {banner && (
        <div
          className={`mb-3 rounded-md px-3 py-2 text-sm ${
            banner.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
              : 'bg-rose-50 text-rose-800 ring-1 ring-rose-200'
          }`}
        >
          {banner.text}
        </div>
      )}

      {/* Paste-grid composer (on-ramp A) */}
      <details className="mb-5 rounded-lg bg-white ring-1 ring-slate-200">
        <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-medium text-slate-700">
          Add vendor product URLs
        </summary>
        <div className="px-4 pb-4">
          <p className="mb-3 text-sm text-slate-500">
            Use this when a vendor page needs product-record review. The server scrapes each URL and adds pending
            proposals only; nothing reaches Odoo until a reviewer approves a row below. Sales-QA does not decide
            package or BOM readiness.
          </p>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={4}
            placeholder={'One vendor product URL per line…\nhttps://owlvans.com/products/…'}
            className="w-full rounded-md ring-1 ring-slate-200 px-2 py-1.5 text-sm font-mono"
          />
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              disabled={busy || pasteText.trim().length === 0}
              onClick={() => void submitPaste()}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              Add to queue
            </button>
            <span className="text-xs text-slate-400">Max 25 per batch. Dealer cost may still need human entry.</span>
          </div>
        </div>
      </details>

      {/* On-ramp tabs */}
      <div className="mb-4 flex max-w-full items-center gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => {
          const count = t.key === 'all' ? proposals.length : proposals.filter((p) => p.source_onramp === t.key).length;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`text-sm px-4 py-2 -mb-px border-b-2 ${
                tab === t.key
                  ? 'border-slate-900 text-slate-900 font-medium'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.label}
              <span className="ml-1.5 text-xs rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-600">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Proposals */}
      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="rounded-lg bg-white p-8 ring-1 ring-slate-200 text-slate-500">
          Product proposal queue is clear. Price sheets, texted URLs, and pasted links land here before any product data reaches Odoo.
        </div>
      ) : (
        <ul className="space-y-2.5">
          {visible.map((p) => (
            <li key={p.id} className="rounded-xl bg-white ring-1 ring-slate-200 p-4">
              <ProposalRow
                p={p}
                editing={editingId === p.id}
                busy={busy}
                onEdit={() => setEditingId(p.id)}
                onCancelEdit={() => setEditingId(null)}
                onSaveEdit={(fields) => void saveEdit(p.id, fields)}
                onApprove={() => void approve(p)}
                onReject={() => void reject(p)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BoundaryCard({
  label,
  title,
  body,
  tone = 'default',
}: {
  label: string;
  title: string;
  body: string;
  tone?: 'default' | 'write';
}): JSX.Element {
  return (
    <div className={`rounded-lg bg-white p-4 ring-1 ${tone === 'write' ? 'ring-rose-200' : 'ring-slate-200'}`}>
      <div className={`text-xs font-semibold uppercase tracking-wide ${tone === 'write' ? 'text-rose-600' : 'text-slate-400'}`}>
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-slate-900">{title}</div>
      <p className="mt-1 text-sm leading-5 text-slate-500">{body}</p>
    </div>
  );
}

function HealthCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'good' | 'warn';
}): JSX.Element {
  const valueCls =
    tone === 'warn' ? 'text-rose-600' : tone === 'good' ? 'text-emerald-600' : 'text-slate-900';
  return (
    <div className={`rounded-lg bg-white ring-1 p-3 ${tone === 'warn' ? 'ring-rose-200' : 'ring-slate-200'}`}>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${valueCls}`}>{value}</div>
    </div>
  );
}

// ── Row ──────────────────────────────────────────────────────────────────────
function ProposalRow({
  p,
  editing,
  busy,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onApprove,
  onReject,
}: {
  p: Proposal;
  editing: boolean;
  busy: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (fields: Record<string, unknown>) => void;
  onApprove: () => void;
  onReject: () => void;
}): JSX.Element {
  const [name, setName] = useState(p.name);
  const [retail, setRetail] = useState(p.retail_price ?? '');
  const [cost, setCost] = useState(p.dealer_cost ?? '');
  const [sku, setSku] = useState(p.sku ?? '');

  useEffect(() => {
    setName(p.name);
    setRetail(p.retail_price ?? '');
    setCost(p.dealer_cost ?? '');
    setSku(p.sku ?? '');
  }, [p.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (editing) {
    return (
      <div className="space-y-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md ring-1 ring-slate-200 px-2 py-1.5 text-sm"
          placeholder="Name"
        />
        <div className="flex flex-wrap gap-2">
          <label className="text-xs text-slate-500">
            Retail
            <input
              value={retail}
              onChange={(e) => setRetail(e.target.value)}
              inputMode="decimal"
              className="ml-1 w-24 rounded-md ring-1 ring-slate-200 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-slate-500">
            Cost
            <input
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              inputMode="decimal"
              className="ml-1 w-24 rounded-md ring-1 ring-slate-200 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-slate-500">
            SKU
            <input
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              className="ml-1 w-32 rounded-md ring-1 ring-slate-200 px-2 py-1 text-sm"
            />
          </label>
        </div>
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              onSaveEdit({
                name,
                retail_price: retail.trim() === '' ? null : Number(retail),
                dealer_cost: cost.trim() === '' ? null : Number(cost),
                sku: sku.trim() === '' ? null : sku.trim(),
              })
            }
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            onClick={onCancelEdit}
            className="rounded-md ring-1 ring-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const marginTone =
    p.margin_pct == null ? 'text-slate-500' : p.thin_margin ? 'text-rose-600' : 'text-emerald-600';

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {p.image_url ? (
          <img src={p.image_url} alt="" className="w-10 h-10 rounded border border-slate-200 object-cover flex-shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded border border-slate-200 bg-slate-50 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="min-w-0 max-w-full truncate text-sm font-medium text-slate-800">{p.name}</span>
            {p.vendor && <span className="text-xs text-slate-400">· {p.vendor}</span>}
            {p.dedup_template_id != null && (
              <span className="text-xs rounded bg-amber-100 text-amber-700 px-1.5 py-0.5" title={`Matches Odoo product #${p.dedup_template_id}`}>
                dupe #{p.dedup_template_id}
              </span>
            )}
            {p.thin_margin && (
              <span className="text-xs rounded bg-rose-100 text-rose-700 px-1.5 py-0.5">thin margin</span>
            )}
            {p.change_type === 'cost_update' && (
              <span className="text-xs rounded bg-sky-100 text-sky-700 px-1.5 py-0.5">
                cost update · Odoo #{p.target_template_id}
              </span>
            )}
            {p.status === 'applying' && (
              <span className="text-xs rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">write in progress</span>
            )}
            {p.status === 'reconciliation_required' && (
              <span className="text-xs rounded bg-rose-100 text-rose-800 px-1.5 py-0.5">reconciliation required</span>
            )}
            <span className="text-xs text-slate-300">{ONRAMP_LABELS[p.source_onramp] ?? p.source_onramp}</span>
          </div>

          {p.change_type === 'cost_update' && (
            <div className="mt-1 text-sm font-medium text-slate-700">
              {money(p.current_cost)} → {money(p.dealer_cost)} cost
              <span className={p.cost_delta_abs != null && p.cost_delta_abs > 0 ? 'text-rose-600' : 'text-emerald-600'}>
                {' '}({signedMoney(p.cost_delta_abs)} / {signedPct(p.cost_delta_pct)})
              </span>
            </div>
          )}

          {/* Margin line */}
          <div className="mt-1 text-sm">
            <span className="text-slate-500">{money(p.retail_price)}</span>
            <span className="text-slate-300"> retail · </span>
            <span className="text-slate-500">{money(p.dealer_cost)}</span>
            <span className="text-slate-300"> cost = </span>
            <span className={`font-semibold ${marginTone}`}>
              {p.margin_abs != null ? `$${p.margin_abs.toFixed(2)}` : '—'} ({pctText(p.margin_pct)})
            </span>
            {p.change_type === 'cost_update' && p.current_margin_pct != null && (
              <span className="text-xs text-slate-500"> · margin {pctText(p.current_margin_pct)} → {pctText(p.margin_pct)}</span>
            )}
            {p.dealer_cost == null && (
              // Phase 1 scrapes retail only — dealer cost needs a vendor login the
              // scraper doesn't do here, so margin can't be computed until a human
              // edits in the cost. Make that explicit rather than showing a silent dash.
              <span className="text-xs text-amber-600"> · add cost to compute margin</span>
            )}
          </div>

          {/* Provenance dots */}
          <div className="mt-1.5 flex items-center gap-3 flex-wrap">
            <ProvenanceDot field="name" source={p.field_provenance.name} />
            <ProvenanceDot field="retail" source={p.field_provenance.retail_price} />
            <ProvenanceDot field="cost" source={p.field_provenance.dealer_cost} />
            <ProvenanceDot field="sku" source={p.field_provenance.sku} />
            {p.source_url && (
              <a href={p.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-sky-600 hover:underline truncate">
                source ↗
              </a>
            )}
          </div>

          {p.scrape_warnings.length > 0 && (
            <div className="mt-1 text-xs text-amber-600">⚠ {p.scrape_warnings[0]}</div>
          )}
          {p.status !== 'pending' && p.review_note && (
            <div className="mt-1 text-xs text-rose-700">{p.review_note}</div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-row gap-2 md:flex-col md:gap-1.5">
        {p.status === 'pending' ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={onApprove}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {p.change_type === 'cost_update' ? 'Update Odoo cost' : 'Create Odoo product'}
            </button>
            <div className="flex gap-1.5">
              <button
                type="button"
                disabled={busy}
                onClick={onEdit}
                className="rounded-md ring-1 ring-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Edit
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={onReject}
                className="rounded-md ring-1 ring-rose-300 px-2.5 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </>
        ) : (
          <div className="max-w-44 rounded-md bg-rose-50 px-3 py-2 text-xs font-medium text-rose-800 ring-1 ring-rose-200">
            Locked pending receipt reconciliation
          </div>
        )}
      </div>
    </div>
  );
}

function pctText(n: number | null): string {
  return n == null ? '—' : `${Math.round(n * 100)}%`;
}

function signedMoney(n: number | null): string {
  if (n == null) return '—';
  return `${n >= 0 ? '+' : '-'}$${Math.abs(n).toFixed(2)}`;
}

function signedPct(n: number | null): string {
  if (n == null) return '—';
  return `${n >= 0 ? '+' : ''}${Math.round(n * 100)}%`;
}
